import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { Aviso, Carregando, Entrada, Etiqueta, Paginacao, Selecao, Vazio } from '../../componentes/ui.jsx';

/**
 * Aba "Pacientes" de Contatos: TODOS os pacientes, cada um com o contato que
 * fala por ele no WhatsApp e todos os numeros desse contato (principal +
 * extras), com FILTROS INTELIGENTES para achar quem interessa agora: quem vem
 * hoje, quem faltou, quem nao tem nada marcado, os menores de idade...
 *
 * Os filtros combinam (Hapvida + Faltaram = pacientes Hapvida que faltaram) e
 * cada um mostra quantos pacientes caem nele (com a busca digitada). A lista e
 * paginada no servidor: 30 por pagina, como todas as listas do sistema.
 *
 * Clicar abre a ficha do CONTATO (onde os numeros se editam), ja filtrada no
 * paciente escolhido.
 */

const PARENTESCO = { proprio: 'o próprio', filho: 'filho(a)', pai_mae: 'pai/mãe', conjuge: 'cônjuge', outro: 'outro' };

/** Os filtros, em grupos. Do mesmo grupo "exclusivo", so um vale por vez (Hapvida OU Particular). */
const GRUPOS = [
  { rotulo: 'Convênio', exclusivo: true, filtros: [['hapvida', 'Hapvida'], ['particular', 'Particular']] },
  { rotulo: 'Agenda', exclusivo: true, filtros: [['hoje', 'Consulta hoje'], ['semana', 'Próximos 7 dias'], ['sem_consulta', 'Sem consulta marcada'], ['faltaram', 'Faltaram (90 dias)']] },
  { rotulo: 'Perfil', exclusivo: true, filtros: [['menores', 'Menores de 18'], ['idosos', '60 anos ou mais']] },
  { rotulo: 'Contato', exclusivo: false, filtros: [['varios_numeros', 'Vários números'], ['dependentes', 'Dependentes'], ['sem_numero', 'Sem número (Hapvida)']] }
];

function quando(ms) {
  const d = new Date(ms);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export function ListaPacientes() {
  const navegar = useNavigate();
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtros, setFiltros] = useState([]);
  const [medico, setMedico] = useState('');
  const [pagina, setPagina] = useState(1);

  // Espera a pessoa parar de digitar: buscar a cada letra seria uma ida ao servidor por tecla.
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 300);
    return () => clearTimeout(t);
  }, [busca]);
  // Mudou o recorte: volta para a primeira pagina.
  useEffect(() => setPagina(1), [buscaAplicada, filtros, medico]);

  const equipe = useQuery({ queryKey: ['profissionais', 'ativos'], queryFn: () => api.get('/api/profissionais') });
  const lista = useQuery({
    queryKey: ['pacientes', 'todos', buscaAplicada, filtros.join(','), medico, pagina],
    queryFn: () => api.get('/api/pacientes', { busca: buscaAplicada, pagina, filtros: filtros.join(','), ...(medico ? { medico } : {}) }),
    placeholderData: (anterior) => anterior
  });

  function alternar(grupo, chave) {
    setFiltros((atual) => {
      if (atual.includes(chave)) return atual.filter((f) => f !== chave);
      const semOGrupo = grupo.exclusivo ? atual.filter((f) => !grupo.filtros.some(([k]) => k === f)) : atual;
      return [...semOGrupo, chave];
    });
  }

  const dados = lista.data;
  const contagens = dados?.contagens ?? {};
  const paginas = dados ? Math.max(1, Math.ceil(dados.total / dados.porPagina)) : 1;
  const algumFiltro = filtros.length > 0 || medico;

  return (
    <section className="cartao">
      <div className="cartao__corpo coluna">
        <div className="pac-busca">
          <Entrada
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por paciente, contato, telefone (inclusive os extras) ou carteirinha"
            aria-label="Buscar pacientes"
          />
          <Selecao value={medico} onChange={(e) => setMedico(e.target.value)} aria-label="Com consulta marcada com">
            <option value="">Qualquer médico</option>
            {(equipe.data?.profissionais ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                Com consulta: {p.nome}
              </option>
            ))}
          </Selecao>
        </div>

        <div className="pac-filtros" aria-label="Filtros">
          {GRUPOS.map((g) => (
            <div key={g.rotulo} className="pac-filtros__grupo">
              <span className="pac-filtros__rotulo">{g.rotulo}</span>
              {g.filtros.map(([chave, rotulo]) => (
                <button
                  key={chave}
                  type="button"
                  aria-pressed={filtros.includes(chave)}
                  className={`eq-chip${filtros.includes(chave) ? ' eq-chip--ativo' : ''}`}
                  onClick={() => alternar(g, chave)}
                >
                  {rotulo}
                  {contagens[chave] != null && <small className="pac-filtros__n">{contagens[chave]}</small>}
                </button>
              ))}
            </div>
          ))}
          {algumFiltro && (
            <button
              type="button"
              className="pac-filtros__limpar"
              onClick={() => {
                setFiltros([]);
                setMedico('');
              }}
            >
              Limpar filtros
            </button>
          )}
        </div>

        {lista.isLoading && <Carregando texto="Carregando pacientes..." />}
        {lista.isError && <Aviso tom="perigo">{lista.error.message}</Aviso>}

        {dados && dados.pacientes.length === 0 && (
          <Vazio
            titulo="Nenhum paciente encontrado"
            descricao={
              algumFiltro || buscaAplicada
                ? 'Nenhum paciente com esses filtros. Tire algum filtro ou mude a busca.'
                : 'Os pacientes aparecem aqui quando são cadastrados num contato ou importados da agenda da Hapvida.'
            }
          />
        )}

        {dados && dados.pacientes.length > 0 && (
          <div className="pac-lista" role="table" aria-label="Pacientes">
            <div className="pac-linha pac-linha--cabecalho" role="row">
              <span role="columnheader">Paciente</span>
              <span role="columnheader">Contato e telefones</span>
              <span role="columnheader">Convênio</span>
              <span role="columnheader">Próxima consulta</span>
            </div>
            {dados.pacientes.map((p) => (
              <button
                key={p.id}
                type="button"
                role="row"
                className="pac-linha"
                onClick={() => navegar(`/contatos/${p.contato.id}?paciente=${p.id}`)}
                title="Abrir a ficha do contato (os números se editam lá)"
              >
                <span role="cell" className="pac-celula">
                  <strong>{p.nomeSocial || p.nome}</strong>
                  <small className="texto-fraco">{p.idade != null ? `${p.idade} anos` : 'idade não informada'}</small>
                </span>
                <span role="cell" className="pac-celula">
                  <span>
                    {p.contato.nome}
                    {p.parentesco !== 'proprio' && <small className="texto-fraco"> · paciente é {PARENTESCO[p.parentesco] ?? p.parentesco}</small>}
                  </span>
                  <span className="pac-telefones">
                    <span className="mono" title="Principal: recebe as mensagens">{p.contato.telefone}</span>
                    {p.contato.telefonesExtras.map((t) => (
                      <span key={t} className="mono texto-fraco" title="Extra: reconhecido quando escreve">
                        {t}
                      </span>
                    ))}
                  </span>
                </span>
                <span role="cell" className="pac-celula">
                  {p.convenio ? <Etiqueta tom="info">{p.convenio}</Etiqueta> : <Etiqueta tom="neutro">Particular</Etiqueta>}
                  {p.carteirinha && <small className="mono texto-fraco">{p.carteirinha}</small>}
                </span>
                <span role="cell" className="pac-celula">
                  {p.proximaConsulta ? (
                    <>
                      <span className="mono">{quando(p.proximaConsulta.inicioEm)}</span>
                      <small className="texto-fraco">{p.proximaConsulta.profissional}</small>
                    </>
                  ) : (
                    <small className="texto-fraco">—</small>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}

        {dados && (
          <Paginacao pagina={pagina} paginas={paginas} total={dados.total} porPagina={dados.porPagina} irPara={setPagina} rotulo="pacientes" />
        )}
      </div>
    </section>
  );
}
